import { z } from 'zod';
import type { TeamRole } from './roles.js';
import type { ThemePref } from './types.js';

/** Hoopify admin console: inputs and response shapes. */

const email = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Enter an email address.')
  .max(254, 'That email address is too long.')
  .email('Enter a valid email address.');
const name = z.string().trim().min(1, 'Enter a name.').max(80, 'Keep the name under 80 characters.');
const password = z.string().min(1, 'Enter a password.').max(128, 'Use no more than 128 characters.');
const code = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app.');
const token = z.string().trim().min(20, 'This link is incomplete. Copy the whole link.').max(200);
/** Why an admin did something. Required for anything that affects a coach. */
const reason = z.string().trim().min(3, 'Say briefly why. It’s kept in the admin log.').max(500, 'Keep the reason under 500 characters.');

export const adminLoginInput = z.object({ email, password });
export const adminCodeInput = z.object({ code });
export const adminSetupStartInput = z.object({ token, name, password });
export const adminSetupFinishInput = z.object({ token, code });
export const adminReasonInput = z.object({ reason });
export const adminNoteInput = z.object({ note: z.string().trim().max(4000, 'Keep the note under 4,000 characters.').transform((v) => v || null) });
export const adminDeleteAccountInput = z.object({ confirmEmail: email, reason });
export const adminTransferTeamInput = z.object({ email, reason });
export const adminInviteInput = z.object({ email, name });
export const adminTestEmailInput = z.object({ to: email });
/** Claiming a new installation with the code shown on the server. */
export const adminClaimInput = z.object({
  code: z.string().trim().min(1, 'Enter the code from the server.').max(30),
  name,
  email,
});

/** Microsoft 365 settings saved from the console. The secret itself is never sent back. */
export interface MailSettingsView {
  tenantId: string;
  clientId: string;
  sender: string;
  replyTo: string | null;
  secretExpires: string | null;
  savedAt: string;
  savedBy: string;
}

const guid = z.string().trim().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'Paste the ID from Entra: it looks like 1a2b3c4d-1234-5678-9abc-1234567890ab.');
export const mailSettingsInput = z.object({
  tenantId: guid,
  clientId: guid,
  /** Blank keeps the secret already saved. */
  clientSecret: z.string().trim().max(200).optional().default(''),
  sender: email,
  replyTo: z.union([email, z.literal('')]).optional().default('').transform((v) => v || null),
  secretExpires: z
    .union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the date the secret expires.'), z.literal('')])
    .optional()
    .default('')
    .transform((v) => v || null),
});
export type MailSettingsInput = z.input<typeof mailSettingsInput>;

/** How the app sends email, and whether it's working. */
export interface MailStatus {
  /** False until email is set up; coach sign-ups and password resets are paused meanwhile. */
  enabled: boolean;
  /** 'console': Microsoft 365 settings saved on the Email page; 'server': the server's settings file. */
  source: 'server' | 'console';
  settings: MailSettingsView | null;
  transport: 'smtp' | 'graph' | 'none';
  label: string;
  from: string;
  replyTo: string | null;
  secretExpires: string | null;
  secretDaysLeft: number | null;
  sent: number;
  failed: number;
  lastOkAt: string | null;
  lastError: { at: string; subject: string; message: string } | null;
}
/** The server's nightly database backup, as the backup service last reported it. */
export type BackupStatus =
  /** Not running on a server with the backup service (e.g. a developer's PC). */
  | { configured: false }
  | {
      configured: true;
      /** null until the first backup has run. */
      ok: boolean | null;
      at: string | null;
      file: string | null;
      bytes: number | null;
      offsite: 'ok' | 'off' | 'failed' | 'skipped' | null;
      message: string | null;
      /** More than 26 hours since the last backup. */
      stale: boolean;
    };

/** What GitHub has that isn't live yet, as the server last found out. */
export interface ServerUpdates {
  checkedAt: string;
  /** A plain-language problem reaching GitHub (e.g. an expired token). */
  error: string | null;
  current: { version: string; subject: string; date: string };
  latest: string;
  behind: number;
  commits: { sha: string; subject: string; date: string }[];
}

/** The latest update run, started from the console or the server command line. */
export interface ServerUpdateRun {
  state: 'running' | 'done' | 'up_to_date' | 'failed' | 'rolled_back';
  step: string;
  message: string;
  from: string;
  to: string;
  startedAt: string;
  updatedAt: string;
}

export type ServerState =
  /** Not running on a server set up with the deploy kit (e.g. a developer's PC). */
  | { available: false; version: string }
  | {
      available: true;
      version: string;
      updates: ServerUpdates | null;
      update: ServerUpdateRun | null;
      /** The last update's output, newest lines last. */
      log: string | null;
      /** Requests waiting for the server to pick them up. */
      requested: { update: boolean; backup: boolean; check: boolean };
    };

export const adminListQuery = z.object({
  q: z.string().trim().max(100).optional().default(''),
  filter: z.string().trim().max(30).optional().default('all'),
  page: z.coerce.number().int().min(1).max(10000).optional().default(1),
});

export const ACCOUNT_FILTERS = ['all', 'suspended', 'unconfirmed', 'no_mfa', 'head_coaches'] as const;
export type AccountFilter = (typeof ACCOUNT_FILTERS)[number];
export const TEAM_FILTERS = ['active', 'archived', 'all'] as const;
export type TeamFilter = (typeof TEAM_FILTERS)[number];
export const ADMIN_PAGE_SIZE = 50;

export interface AdminMe {
  id: string;
  name: string;
  email: string;
  /** Gets an email when a new coach signs up. */
  notifyNewCoaches: boolean;
  /** Their light/dark choice, saved on the account. Null until they choose. */
  theme: ThemePref | null;
}

export const adminNotificationsInput = z.object({ newCoaches: z.boolean() });
export type AdminNotificationsInput = z.infer<typeof adminNotificationsInput>;

export type AdminSessionState = { status: 'signed_in'; admin: AdminMe } | { status: 'mfa_required' } | { status: 'signed_out' };

export interface AdminSetupPreview {
  email: string;
  name: string;
}
export interface AdminSetupStart {
  secret: string;
  otpauthUrl: string;
  qrSvg: string;
}

export interface AdminOverview {
  accounts: { total: number; confirmed: number; withMfa: number; suspended: number; headCoaches: number };
  active: { last7: number; last30: number };
  teams: { active: number; archived: number };
  sessions: { total: number; next30: number; last30: number };
  /** Oldest first, Monday-starting weeks. */
  signupsByWeek: { week: string; count: number }[];
  recentSignups: AdminAccountRow[];
}

export interface Page<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminAccountRow {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  lastSeenAt: string | null;
  emailConfirmed: boolean;
  mfa: boolean;
  suspended: boolean;
  teamCount: number;
  headCoachOf: number;
}

export interface AdminAccountDetail extends AdminAccountRow {
  suspendedAt: string | null;
  suspendedReason: string | null;
  note: string | null;
  locked: boolean;
  teams: { id: string; name: string; role: TeamRole; archived: boolean; memberCount: number }[];
  log: AdminLogEntry[];
}

export interface AdminTeamRow {
  id: string;
  name: string;
  ageGroup: string | null;
  headCoach: { id: string; name: string; email: string } | null;
  memberCount: number;
  sessionCount: number;
  createdAt: string;
  archivedAt: string | null;
}

export interface AdminTeamDetail extends AdminTeamRow {
  members: { userId: string; name: string; email: string; role: TeamRole; joinedAt: string }[];
  counts: { sessions: number; published: number; rosterPlayers: number; seasons: number; drillsShared: number };
  lastActivityAt: string | null;
  log: AdminLogEntry[];
}

export interface AdminLogEntry {
  id: string;
  adminName: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  summary: string;
  reason: string | null;
  createdAt: string;
}

export interface AdminRow {
  id: string;
  name: string;
  email: string;
  /** False until they finish setup from their link. */
  ready: boolean;
  setupExpiresAt: string | null;
  lastSignInAt: string | null;
  createdAt: string;
  isMe: boolean;
}

/** Returned when an admin is added or reset: the link to send them (also emailed). */
export interface AdminSetupLink {
  admin: AdminRow;
  setupUrl: string;
}
